import { InvalidCross, ValidCheck } from "@assets";
import { Row } from "@components/Flex";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Caption } from "@components/Typography";
import { useTheme } from "@hooks";
import { Translations, i18n } from "@i18n";
import { validatePassword } from "@utils";
import { Container, SubSection, ValidatorCaption, useStyles } from "./styles";
import { PasswordValidatorProps, ValidatorItemProps } from "./types";

export const PasswordValidator = ({ password = "", confirmPassword }: PasswordValidatorProps) => {
  const styles = useStyles();

  const {
    hasMinCharacters,
    hasMinimumValidations,
    hasLowerCaseCharacter,
    hasUpperCaseCharacter,
    hasNumber,
    hasSpecialCharacter,
  } = validatePassword(password);

  // Only check password match if confirmPassword is provided
  const passwordsMatch = confirmPassword !== undefined ? password === confirmPassword : true;
  const showPasswordMatchValidator = confirmPassword !== undefined;

  return (
    <>
      <Container style={styles.container}>
        <Spacing size={SpacingSizes.M} />
        <Caption>{i18n.t(Translations.PasswordValidatorTitle)}</Caption>
        <Spacing size={SpacingSizes.XS} />

        <ValidatorItem valid={hasMinCharacters}>
          {i18n.t(Translations.PasswordValidatorMinCharacters)}
        </ValidatorItem>
        <Spacing size={SpacingSizes.XS} />

        <ValidatorItem valid={hasMinimumValidations}>
          {i18n.t(Translations.PasswordValidatorMinValidations)}
        </ValidatorItem>
        <Spacing size={SpacingSizes.XS} />

        <SubSection>
          <ValidatorItem valid={hasLowerCaseCharacter}>
            {i18n.t(Translations.PasswordValidatorLowerCase)}
          </ValidatorItem>
          <Spacing size={SpacingSizes.XS} />

          <ValidatorItem valid={hasUpperCaseCharacter}>
            {i18n.t(Translations.PasswordValidatorUpperCase)}
          </ValidatorItem>
          <Spacing size={SpacingSizes.XS} />

          <ValidatorItem valid={hasNumber}>
            {i18n.t(Translations.PasswordValidatorNumbers)}
          </ValidatorItem>
          <Spacing size={SpacingSizes.XS} />

          <ValidatorItem valid={hasSpecialCharacter}>
            {i18n.t(Translations.PasswordValidatorSpecialCharacters)}
          </ValidatorItem>
          <Spacing size={SpacingSizes.XS} />
        </SubSection>
        <Spacing size={SpacingSizes.XS} />

        {showPasswordMatchValidator && (
          <>
            <ValidatorItem valid={passwordsMatch}>
              {i18n.t(Translations.PasswordValidatorMatchPassword)}
            </ValidatorItem>
            <Spacing size={SpacingSizes.XS} />
          </>
        )}
      </Container>
      <Spacing size={SpacingSizes.M} />
    </>
  );
};

const ValidatorItem = ({ valid, children }: ValidatorItemProps) => {
  const { theme } = useTheme();

  const color = valid ? theme.validator.valid : theme.validator.invalid;

  const icon = valid ? <ValidCheck /> : <InvalidCross />;

  return (
    <Row>
      {icon}
      <ValidatorCaption style={{ color }}>{children}</ValidatorCaption>
    </Row>
  );
};
